// A leaf option contributes a single value; a group option (e.g. the
// "Elementary (K–5)" grade band) stands for several values at once.
// `count` is the number of results the option leads to; a 0 disables it unless it's already
// (partly) selected, so a selection can always be undone.
export type FilterMultiSelectOption =
  | { label: string; value: string; count?: number }
  | { label: string; values: string[]; count?: number }

export interface FilterMultiSelectSection {
  title?: string
  options: FilterMultiSelectOption[]
}

export function isGroupOption(
  option: FilterMultiSelectOption
): option is { label: string; values: string[]; count?: number } {
  return 'values' in option
}

// Checkbox state for an option given the currently selected values.
//
// Leaf options are simply checked when their value is selected. Group options
// are the interesting case:
//   - checked        → every one of the group's values is selected
//   - indeterminate  → some (but not all) of the group's values are selected
//
// `checked` and `indeterminate` are mutually exclusive — MUI renders the
// indeterminate dash with priority over the checkmark.
export function getOptionState(
  option: FilterMultiSelectOption,
  selected: string[]
): { checked: boolean; indeterminate: boolean } {
  if (!isGroupOption(option)) {
    return { checked: selected.includes(option.value), indeterminate: false }
  }

  const selectedCount = option.values.filter((value) =>
    selected.includes(value)
  ).length
  const checked =
    option.values.length > 0 && selectedCount === option.values.length

  return { checked, indeterminate: selectedCount > 0 && !checked }
}

export function isOptionDisabled(
  option: FilterMultiSelectOption,
  selected: string[]
): boolean {
  const { checked, indeterminate } = getOptionState(option, selected)
  return option.count === 0 && !checked && !indeterminate
}

// The next selection after clicking an option.
//
// Leaf: plain toggle. Group: if every value of the group is already selected,
// clicking deselects all of them; otherwise it selects all of them (filling in
// the missing ones). Un-checking a single grade inside a checked band is then
// handled naturally: the band stops being "fully selected" and renders
// indeterminate, while the other grades stay selected.
export function toggleOption(
  option: FilterMultiSelectOption,
  selected: string[]
): string[] {
  if (!isGroupOption(option)) {
    return selected.includes(option.value)
      ? selected.filter((value) => value !== option.value)
      : [...selected, option.value]
  }

  const { checked } = getOptionState(option, selected)
  const withoutGroup = selected.filter(
    (value) => !option.values.includes(value)
  )
  return checked ? withoutGroup : [...withoutGroup, ...option.values]
}

// The display labels used for the active-chip summary.
//
// Walks every section's options against the selected values and returns the
// labels to show, honoring the "collapse full groups" rule:
//   - a group option whose values are ALL selected contributes its own label
//     (e.g. "Elementary (K–5)") and swallows its values, so they don't also
//     appear individually
//   - any remaining selected value contributes the label of its leaf option
//     (a partially-selected group is NOT collapsed — its selected values show
//     individually)
//
// Output is ordered the way the options are declared (sections top-to-bottom,
// options within a section in order), not the order the user clicked things.
export function getSummaryLabels(
  sections: FilterMultiSelectSection[],
  selected: string[]
): string[] {
  const options = sections.flatMap((section) => section.options)

  // Values represented by a fully-selected group; they must not also surface
  // as individual leaf labels.
  const collapsed = new Set(
    options
      .filter(isGroupOption)
      .filter((group) => getOptionState(group, selected).checked)
      .flatMap((group) => group.values)
  )

  return options.flatMap((option) => {
    if (isGroupOption(option)) {
      return getOptionState(option, selected).checked ? [option.label] : []
    }
    return selected.includes(option.value) && !collapsed.has(option.value)
      ? [option.label]
      : []
  })
}
